import { useState, useEffect, useMemo } from "react";
import * as BsIcons from "react-bootstrap-icons";
import Swal from "sweetalert2";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";
import ModalProyecto from "./ModalProyecto";
import ModalGrupo from "./ModalGrupo";

const SidebarTareas = ({
  groups = [],
  setGroups,
  onSelectProject,
  usuarios = [],
  projectToEdit,
}) => {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user?.uid;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupShape, setGroupShape] = useState("");
  const [groupColor, setGroupColor] = useState("#007bff");
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [visibility, setVisibility] = useState("solo etiquetados");
  const [assignedTo, setAssignedTo] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroupShape, setSelectedGroupShape] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notifyMode, setNotifyMode] = useState("");
  const [notifyQty, setNotifyQty] = useState(1);
  const [notifyUnit, setNotifyUnit] = useState("Days");
  const [openFilters, setOpenFilters] = useState({});

  const ownerUser = {
    id: user.uid,
    nombre: user.nombre,
    apellido: user.apellido,
    avatarUrl: user.avatarUrl || "/img/avatar-default.png",
  };

  const teamOwnerUser =
    teamOwnerId === user.uid
      ? ownerUser
      : usuarios.find((u) => u.id === teamOwnerId) || {
          id: teamOwnerId,
          nombre: "Nombre genérico",
          apellido: "",
          avatarUrl: "/img/avatar-default.png",
        };

  // Abrir modal de edición
  useEffect(() => {
    if (projectToEdit && projectToEdit.modo === "editar") {
      setProjectName(projectToEdit.projectName || "");
      setDescription(projectToEdit.description || "");
      setPriority(projectToEdit.priority || "Normal");
      setStartDate(projectToEdit.startDate || "");
      setDueDate(projectToEdit.dueDate || "");
      setSelectedGroupId(projectToEdit.groupId || "");
      setSelectedGroupShape(projectToEdit.shape || ""); // <-- AGRÉGALO AQUÍ
      setTags(projectToEdit.tags || []);
      setAssignedTo(projectToEdit.assignedTo || []);
      setProjectModalOpen(true);
    }
  }, [projectToEdit]);

  useEffect(() => {
    if (groups.length === 0) return;

    const state = {};
    groups.forEach((g) => {
      state[g.id] = true; // <-- marca TODOS como abiertos
    });
    setOpenGroups(state);
  }, [groups]);

  const filtrosOpts = ["Favoritos", "Urgentes", "Programados", "Completos"];
  const [filters] = useState(
    filtrosOpts.map((name) => ({
      id: name,
      name,
      items: [],
    }))
  );

  const allProjects = useMemo(
    () =>
      groups.flatMap((g) =>
        (g.projects || []).map((p) => ({
          ...p,
          groupId: g.id,
          groupColor: g.color, // opcional para colorear iconos
        }))
      ),
    [groups]
  );

  const FILTER_RULES = {
    Favoritos: (p) => !!p.favorito, // puede estar completo
    Urgentes: (p) => !!p.urgente && !p.completo, // ocultar si está completo
    Programados: (p) => !!p.programado && !p.completo, // ocultar si está completo
    Completos: (p) => !!p.completo,
  };

  const getItemsForFilter = (name) =>
    allProjects.filter(FILTER_RULES[name] || (() => false));

  // Inicializa openFilters (abierto solo si hay items)
  useEffect(() => {
    const init = {};
    filtrosOpts.forEach((name) => {
      const hasItems = getItemsForFilter(name).length > 0;
      init[name] = hasItems; // abre los que tengan algo
    });
    setOpenFilters(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProjects.length]); // se recalcula cuando cambia el total

  const handleGroupClick = (groupId) => {
    setOpenGroups((prev) => {
      const isOpen = !prev[groupId]; // invertimos solo ese
      // si se abre -> seleccionarlo. Si se cierra -> limpiar selección.
      if (isOpen) {
        setSelectedGroupId(groupId);
      } else {
        setSelectedGroupId(null);
      }
      return { ...prev, [groupId]: isOpen };
    });
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setGroupName("");
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      Swal.fire({
        title: "Error!",
        text: "Ingresa un nombre de grupo",
        icon: "error",
        scrollbarPadding: false,
      });
      return;
    }

    if (!groupShape || groupShape === "") {
      Swal.fire({
        title: "Error!",
        text: "Selecciona un icono para el grupo",
        icon: "error",
        scrollbarPadding: false,
      });
      return;
    }

    try {
      const newGroup = {
        name: groupName,
        shape: groupShape,
        color: groupColor,
        ownerId: ownerUid,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "gruposTareas"), newGroup);

      closeModal();
      Swal.fire({
        title: "¡Creado!",
        text: "¡Elemento agregado exitosamente!",
        icon: "success",
      });
    } catch (error) {
      console.error("Error creando grupo:", error);
      Swal.fire({
        title: "Error!",
        text: "Ocurrió un error al crear el grupo",
        icon: "error",
      });
    }
  };

  const openProjectModal = (groupId, shapeValue) => {
    // Asignar el grupo al que se va a crear el proyecto
    setSelectedGroupId(groupId);
    setSelectedGroupShape(shapeValue);

    // Cuando abras el modal desde el botón "+", fuerza el modo CREAR
    if (projectToEdit) {
      projectToEdit.modo = "crear";
    }

    // Prellenar fecha de hoy
    const today = new Date().toISOString().slice(0, 10);
    setStartDate(today);

    // Asignar usuarios por defecto
    setAssignedTo(
      ownerUser.id === teamOwnerUser.id
        ? [ownerUser]
        : [ownerUser, teamOwnerUser]
    );

    // Abrir modal
    setProjectModalOpen(true);
  };

  const closeProjectModal = () => {
    setProjectModalOpen(false);
    setProjectName("");
    setDescription("");
    setPriority("Normal");
    setStartDate("");
    setDueDate("");
    setVisibility("solo etiquetados");
    setNotifyMode("");
    setNotifyQty(1);
    setNotifyUnit("Days");
    setAssignedTo(
      ownerUser.id === teamOwnerUser.id
        ? [ownerUser]
        : [ownerUser, teamOwnerUser]
    );
    setTags([]);
  };

  const handleCreateProject = async (groupLocalId) => {
    if (!projectName.trim()) {
      Swal.fire({
        title: "Error!",
        text: "Ingresa el nombre del proyecto",
        icon: "error",
      });
      return;
    }

    try {
      const assignedWithAvatar = (assignedTo || []).map((u) => ({
        id: u.id || "",
        nombre: u.nombre || "",
        apellido: u.apellido || "",
        email: u.email || "",
        avatarUrl: u.avatarUrl || "/img/avatar2.jpg",
      }));

      if (!groupLocalId) {
        Swal.fire("Error!", "Debes seleccionar un grupo", "error");
        return;
      }

      const data = {
        projectName,
        description,
        priority,
        visibility,
        assignedTo: assignedWithAvatar,
        startDate: startDate || null,
        dueDate: dueDate || null,
        tags: Array.isArray(tags) ? tags : [],
        shape: selectedGroupShape || projectToEdit?.shape,
        groupId: groupLocalId,
        ownerId: ownerUid,
      };

      // Si venimos a EDITAR
      if (
        projectToEdit &&
        projectToEdit?.id &&
        projectToEdit?.modo === "editar"
      ) {
        const oldGroupId = projectToEdit.groupId;
        const newGroupId = groupLocalId;
        const docId = projectToEdit.id;

        // Detectar si cambió de grupo
        if (oldGroupId !== newGroupId) {
          // 1. Crear en el nuevo grupo USANDO EL MISMO ID
          await setDoc(doc(db, "gruposTareas", newGroupId, "projects", docId), {
            ...data,
            createdAt: projectToEdit.createdAt || serverTimestamp(),
          });
          // 2. Borrar del viejo grupo
          await deleteDoc(
            doc(db, "gruposTareas", oldGroupId, "projects", docId)
          );
        } else {
          // Mismo grupo -> solo actualizarlo
          await updateDoc(
            doc(db, "gruposTareas", oldGroupId, "projects", docId),
            data
          );
        }
        Swal.fire(
          "Actualizado",
          "Proyecto actualizado correctamente",
          "success"
        );
      }

      // Crear nuevo
      else {
        await addDoc(collection(db, "gruposTareas", data.groupId, "projects"), {
          ...data,
          createdAt: serverTimestamp(),
        });
        Swal.fire(
          "Proyecto creado",
          "Proyecto creado correctamente",
          "success"
        );
      }

      closeProjectModal();
    } catch (error) {
      console.error("Error:", error);
      Swal.fire("Error", "No se pudo guardar el proyecto", "error");
    }
  };

  return (
    <>
      <div className="sidebar-tareas">
        <div
          className="tareas-header"
          style={{ cursor: "pointer" }}
          onClick={() => onSelectProject({ tipo: "todas" })}
        >
          <i className="bi bi-list"></i>
          <span style={{ marginLeft: "5px" }}>Todas</span>
        </div>

        <div className="section-header">
          <h4 className="sidebar-section-title">Filtros</h4>
        </div>

        <ul className="list-tareas">
          {filters.map((f) => {
            const iconMap = {
              Favoritos: "bi-star-fill",
              Urgentes: "bi-exclamation-circle-fill",
              Programados: "bi-clock-history",
              Completos: "bi-check2-square",
              Eliminados: "bi-trash",
            };
            const colorMap = {
              Favoritos: "#FFC107",
              Urgentes: "#EB5757",
              Programados: "#C75BF2",
              Completos: "#27AE60",
              Eliminados: "#B0B7C3",
            };

            const items = getItemsForFilter(f.name); // <- genérico
            const hasItems = items.length > 0;
            const isOpen = !!openFilters[f.name];

            return (
              <li key={f.id}>
                <div
                  className="sidebar-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: hasItems ? "pointer" : "default",
                  }}
                  onClick={() => {
                    if (hasItems) {
                      setOpenFilters((prev) => ({
                        ...prev,
                        [f.name]: !prev[f.name],
                      }));
                    }
                  }}
                >
                  {/* Chevron a la IZQUIERDA (o espacio si no hay items) */}
                  {hasItems ? (
                    <i
                      className={`chevron bi ${
                        isOpen ? "bi-chevron-down" : "bi-chevron-right"
                      }`}
                    />
                  ) : (
                    <span className="chevron" />
                  )}

                  {/* Icono del filtro */}
                  <i
                    className={`bi ${iconMap[f.name]}`}
                    style={{ color: colorMap[f.name], fontSize: "16px" }}
                  />
                  <span>{f.name}</span>
                </div>

                {/* Desplegable */}
                {hasItems && isOpen && (
                  <ul className="sub-list">
                    {items.map((item) => {
                      const IconProject = BsIcons[item.shape];
                      return (
                        <li
                          key={item.id}
                          className="project-item"
                          onClick={() => onSelectProject({ ...item })}
                        >
                          {IconProject ? (
                            <IconProject style={{ color: item.groupColor }} />
                          ) : (
                            <i
                              className="bi bi-circle-fill"
                              style={{ fontSize: 10 }}
                            />
                          )}
                          <span style={{ fontSize: 14 }}>
                            {item.projectName}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        <div className="section-header grupos-header">
          <h4 className="sidebar-section-title">Grupos</h4>
          <button
            className="add-btn"
            onClick={openModal}
            title="Agregar en Grupos"
          >
            <i className="bi bi-plus" />
          </button>
        </div>

        <ul className="list-tareas grupos-list">
          {groups.map((group) => {
            const IconGroup = BsIcons[group.shape];

            // Proyectos que se muestran (aquí escondes los completados)
            const visibleProjects = (group.projects || []).filter(
              (p) => !p.completo
            );
            const hasProjects = visibleProjects.length > 0;
            const isOpen = !!openGroups[group.id];

            return (
              <li key={group.id}>
                <div className="grupo-item">
                  <div
                    className="grupo-toggle"
                    onClick={() => {
                      if (hasProjects) handleGroupClick(group.id);
                    }}
                    style={{ cursor: hasProjects ? "pointer" : "default" }}
                  >
                    {/* Chevron solo si hay proyectos; si no, placeholder para alinear */}
                    {hasProjects ? (
                      <i
                        className={`chevron bi ${
                          isOpen ? "bi-chevron-down" : "bi-chevron-right"
                        }`}
                      />
                    ) : (
                      <span className="chevron" />
                    )}

                    {IconGroup ? (
                      <IconGroup style={{ color: group.color }} />
                    ) : (
                      <span
                        className={group.shape}
                        style={{ color: group.color }}
                      />
                    )}
                    <span className="grupo-label">{group.name}</span>
                  </div>

                  <button
                    className="add-btn"
                    onClick={() => openProjectModal(group.id, group.shape)}
                    title="Agregar proyecto"
                  >
                    <i className="bi bi-plus"></i>
                  </button>
                </div>

                {/* Solo despliega si HAY proyectos y el grupo está abierto */}
                {hasProjects && isOpen && (
                  <ul className="sub-list">
                    {visibleProjects.map((p) => {
                      const IconProject = BsIcons[p.shape];
                      return (
                        <li
                          key={p.id}
                          className="project-item"
                          onClick={() =>
                            onSelectProject({ ...p, groupId: group.id })
                          }
                        >
                          {IconProject ? (
                            <IconProject style={{ color: group.color }} />
                          ) : (
                            <span
                              className={p.shape}
                              style={{ color: group.color }}
                            />
                          )}
                          {p.projectName}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <ModalGrupo
        isOpen={isModalOpen}
        closeModal={closeModal}
        groupName={groupName}
        setGroupName={setGroupName}
        groupShape={groupShape}
        setGroupShape={setGroupShape}
        setGroupColor={setGroupColor}
        handleSaveGroup={handleSaveGroup}
      />

      <ModalProyecto
        isOpen={projectModalOpen}
        closeModal={closeProjectModal}
        projectName={projectName}
        modo={projectToEdit?.modo || "crear"}
        setProjectName={setProjectName}
        description={description}
        setDescription={setDescription}
        owner={ownerUser}
        teamOwner={teamOwnerUser}
        assignees={assignedTo}
        onAddMember={(user) =>
          setAssignedTo((prev) =>
            prev.some((u) => u.id === user.id) ? prev : [...prev, user]
          )
        }
        priority={priority}
        setPriority={setPriority}
        startDate={startDate}
        setStartDate={setStartDate}
        dueDate={dueDate}
        setDueDate={setDueDate}
        notifyMode={notifyMode}
        setNotifyMode={setNotifyMode}
        notifyQty={notifyQty}
        setNotifyQty={setNotifyQty}
        notifyUnit={notifyUnit}
        setNotifyUnit={setNotifyUnit}
        handleSaveProject={handleCreateProject}
        usuarios={usuarios}
        grupos={groups}
        selectedGroupId={selectedGroupId}
        setSelectedGroupId={setSelectedGroupId}
        tags={tags}
        setTags={setTags}
      />
    </>
  );
};

export default SidebarTareas;
